/**
 * Filiation N-N d'une SKILLCARD (migration 0283) — fiche auteur.
 *
 * Liste les cartes mères (retirables) et les cartes dérivées, et permet
 * d'ajouter une mère supplémentaire parmi MES cartes (ni elle-même, ni une
 * descendante, ni une mère déjà posée, ni archivée). Le serveur impose la
 * rareté (plus rare des mères + 1 étoile) et refuse les cycles.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { MathText } from '@jeraisonne/ui';
import { CARD_RARITY_LABELS, stripMath } from '@jeraisonne/db/cards';
import {
  useAddCardParent,
  useCardRelations,
  useCards,
  useRemoveCardParent,
  type CardRow,
} from '@/lib/cards/api';
import {
  childIdsOf,
  parentCandidates,
  parentIdsOf,
  rarityFromRelations,
} from '@/lib/cards/filiation';
import {
  SkillcardPickerField,
  toSkillcardPickerItem,
} from '@/features/skillcards/skillcard-picker';

export function CardFiliationEditor({ card }: { card: CardRow }) {
  const cards = useCards();
  const relations = useCardRelations();
  const add = useAddCardParent();
  const remove = useRemoveCardParent();
  const [error, setError] = useState<string | null>(null);

  const all = cards.data ?? [];
  const edges = relations.data ?? [];
  const byId = new Map(all.map((c) => [c.id, c]));
  const parents = parentIdsOf(edges, card.id)
    .map((id) => byId.get(id))
    .filter((c): c is CardRow => !!c);
  const children = childIdsOf(edges, card.id)
    .map((id) => byId.get(id))
    .filter((c): c is CardRow => !!c);
  const candidates = parentCandidates(all, edges, card.id, card.subject);
  const forced = rarityFromRelations(all, edges, card.id);

  const onAdd = async (parentId: string) => {
    setError(null);
    try {
      await add.mutateAsync({ parentId, childId: card.id });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onRemove = async (parentId: string) => {
    setError(null);
    try {
      await remove.mutateAsync({ parentId, childId: card.id });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-2" data-testid="card-filiation-editor">
      <div>
        <p className="text-ink-500 text-xs">
          Cartes mères {forced && `· rareté imposée : ${CARD_RARITY_LABELS[forced]}`}
        </p>
        {parents.length === 0 ? (
          <p className="text-ink-500 text-sm">Aucune — carte de départ.</p>
        ) : (
          <ul className="space-y-1">
            {parents.map((p) => (
              <li key={p.id} className="flex items-center gap-2">
                <Link
                  to="/tutor/cards/$cardId"
                  params={{ cardId: p.id }}
                  className="text-primary-600 min-w-0 flex-1 truncate text-sm font-semibold underline"
                >
                  <MathText>{p.title}</MathText>
                </Link>
                <button
                  type="button"
                  aria-label={`Retirer la carte mère ${stripMath(p.title)}`}
                  disabled={remove.isPending}
                  onClick={() => void onRemove(p.id)}
                  className="text-ink-400 hover:text-accent-600 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg disabled:opacity-40"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Choisir une carte l'ajoute aussitôt comme mère (sélecteur en modale). */}
      <SkillcardPickerField
        items={candidates.map(toSkillcardPickerItem)}
        value={null}
        onChange={(id) => {
          if (id) void onAdd(id);
        }}
        placeholder="Ajouter une carte mère…"
        title="Ajouter une carte mère"
        disabled={add.isPending}
      />
      {error && (
        <p role="alert" className="text-accent-600 text-sm">
          {error}
        </p>
      )}

      {children.length > 0 && (
        <div>
          <p className="text-ink-500 text-xs">Cartes dérivées</p>
          <p className="flex flex-wrap gap-x-3 gap-y-1">
            {children.map((c) => (
              <Link
                key={c.id}
                to="/tutor/cards/$cardId"
                params={{ cardId: c.id }}
                className="text-primary-600 text-sm font-semibold underline"
              >
                <MathText>{c.title}</MathText>
              </Link>
            ))}
          </p>
        </div>
      )}
    </div>
  );
}
